import { useRef, useState, type FormEvent } from "react";
import { Download, KeyRound, LockKeyhole, Upload } from "lucide-react";

import { api } from "../api";

type Props = {
  setupRequired: boolean;
  setupTokenRequired: boolean;
  onAuthenticated: () => void;
};

export function AuthScreen({ setupRequired, setupTokenRequired, onAuthenticated }: Props) {
  const [mode, setMode] = useState<"login" | "recover">(setupRequired ? "login" : "login");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [recoveryToken, setRecoveryToken] = useState("");
  const [setupToken, setSetupToken] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const recoveryInput = useRef<HTMLInputElement>(null);

  const downloadRecovery = (content: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: "text/plain;charset=utf-8" }));
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "supergpt-recovery.txt";
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    if ((setupRequired || mode === "recover") && password !== confirm) {
      setError("Пароли не совпадают");
      return;
    }
    setBusy(true);
    try {
      if (setupRequired) {
        const result = await api.setup(password, setupToken);
        downloadRecovery(result.recoveryFile);
      } else if (mode === "recover") {
        await api.recover(recoveryToken, password);
      } else {
        await api.login(password);
      }
      onAuthenticated();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Не удалось войти");
    } finally {
      setBusy(false);
    }
  };

  const readRecoveryFile = async (file?: File) => {
    if (!file) return;
    const lines = (await file.text()).split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
    setRecoveryToken(lines.at(-1) ?? "");
  };

  return (
    <main className="auth-page">
      <section className="auth-shell" aria-labelledby="auth-title">
        <div className="auth-brand">
          <span className="brand-mark large">S</span>
          <div>
            <strong>SuperGPT</strong>
            <p>Личное пространство</p>
          </div>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <div className="form-heading">
            <span className="auth-icon" aria-hidden="true">
              {setupRequired ? <KeyRound /> : <LockKeyhole />}
            </span>
            <div>
              <h1 id="auth-title">
                {setupRequired ? "Задайте пароль" : mode === "recover" ? "Восстановление" : "Вход"}
              </h1>
              <p>{setupRequired ? "Пароль понадобится при каждом новом входе" : "Введите пароль SuperGPT"}</p>
            </div>
          </div>

          {setupRequired && setupTokenRequired && (
            <label>
              Код первого запуска
              <input autoFocus autoComplete="one-time-code" value={setupToken} onChange={(event) => setSetupToken(event.target.value)} required />
            </label>
          )}

          {mode === "recover" && !setupRequired && (
            <label>
              Recovery-файл
              <input ref={recoveryInput} hidden type="file" accept=".txt,text/plain" onChange={(event) => void readRecoveryFile(event.target.files?.[0])} />
              <button className="button button-glass button-full" type="button" onClick={() => recoveryInput.current?.click()}>
                <Upload /> {recoveryToken ? "Файл выбран" : "Выбрать файл"}
              </button>
            </label>
          )}

          <label>
            {setupRequired || mode === "recover" ? "Новый пароль" : "Пароль"}
            <input autoFocus={!setupTokenRequired} type="password" autoComplete={setupRequired ? "new-password" : "current-password"} minLength={setupRequired || mode === "recover" ? 10 : 1} value={password} onChange={(event) => setPassword(event.target.value)} required />
          </label>

          {(setupRequired || mode === "recover") && (
            <label>
              Повторите пароль
              <input type="password" autoComplete="new-password" minLength={10} value={confirm} onChange={(event) => setConfirm(event.target.value)} required />
            </label>
          )}

          {setupRequired && (
            <div className="recovery-note">
              <Download aria-hidden="true" />
              <span>Recovery-файл скачается после настройки.</span>
            </div>
          )}
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="button button-primary button-full" disabled={busy} type="submit">
            {busy ? "Подождите..." : setupRequired ? "Создать пароль" : mode === "recover" ? "Сбросить пароль" : "Войти"}
          </button>
          {!setupRequired && (
            <button className="text-button" type="button" onClick={() => { setMode(mode === "recover" ? "login" : "recover"); setError(""); }}>
              {mode === "recover" ? "Вернуться ко входу" : "Забыли пароль?"}
            </button>
          )}
        </form>
      </section>
    </main>
  );
}
